import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ClipboardList,
  Eye,
  HandCoins,
  PencilLine,
  XCircle,
} from 'lucide-react';
import { ActionIconButton, TableEmptyOverlay } from '../common';
import PurchaseOrderStatusBadge from './PurchaseOrderStatusBadge';

function PurchaseOrderTable({
  purchaseOrders,
  balancingRecords = [],
  warehouseLocationOptions = [],
  deliveryDateSort,
  onEditPurchaseOrder,
  onOpenProgress,
  onOpenSupplierBalancing,
  onCancelPurchaseOrder,
  cancelingPurchaseOrderId,
  onToggleDeliveryDateSort,
}) {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>P.O. Code</TableHeading>
              <TableHeading>P.O. Date</TableHeading>
              <TableHeading>
                <button
                  className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 uppercase tracking-[0.04em] transition ${
                    deliveryDateSort === 'default'
                      ? '-ml-2 text-[var(--theme-accent-panel-text)] hover:bg-white/15'
                      : '-ml-2 bg-white text-[var(--theme-accent)]'
                  }`}
                  type="button"
                  onClick={onToggleDeliveryDateSort}
                  aria-label={getDeliveryDateSortLabel(deliveryDateSort)}
                  title={getDeliveryDateSortLabel(deliveryDateSort)}
                >
                  Delivery Date
                  <DeliveryDateSortIcon sort={deliveryDateSort} />
                </button>
              </TableHeading>
              <TableHeading>
                <span className="inline-flex items-baseline gap-1">
                  <span>Supplier</span>
                  <span className="text-[0.56rem] font-medium normal-case tracking-normal text-[var(--theme-accent-panel-text)] opacity-75">
                    (Sacks Mark+Company)
                  </span>
                </span>
              </TableHeading>
              <TableHeading>Price</TableHeading>
              <TableHeading>Quantity</TableHeading>
              <TableHeading>Branch</TableHeading>
              <TableHeading>Status</TableHeading>
              <TableHeading>Actions</TableHeading>
            </tr>
          </thead>
          <tbody>
            {purchaseOrders.length ? (
              purchaseOrders.map((purchaseOrder) => {
                const branchValue = formatBranchValue(
                  purchaseOrder,
                  warehouseLocationOptions
                );

                return (
                  <tr
                    key={purchaseOrder.id}
                    className={`border-t border-[var(--theme-border-soft)] transition-colors ${
                      isDeliveryToday(purchaseOrder.deliveryDate)
                        ? 'bg-[#fff6e2] hover:bg-[#ffefc8]'
                        : 'hover:bg-[var(--theme-surface-soft)]'
                    }`}
                  >
                    <TableCell>{formatPurchaseOrderCode(purchaseOrder)}</TableCell>
                    <TableCell>{formatDate(purchaseOrder.poDate)}</TableCell>
                    <TableCell>
                      <DeliveryDateValue value={purchaseOrder.deliveryDate} />
                    </TableCell>
                    <TableCell>
                      <span
                        className="block max-w-[180px] truncate"
                        title={formatSupplierDisplay(purchaseOrder.sacksMark, purchaseOrder.supplierName)}
                      >
                        {formatSupplierDisplay(purchaseOrder.sacksMark, purchaseOrder.supplierName)}
                      </span>
                    </TableCell>
                    <TableCell>{formatPurchaseOrderPrice(getDisplayPrice(purchaseOrder))}</TableCell>
                    <TableCell>{formatStockValue(getDisplayQuantity(purchaseOrder), purchaseOrder.stockUnit)}</TableCell>
                    <TableCell>
                      <span className="block max-w-[110px] truncate" title={branchValue}>
                        {branchValue}
                      </span>
                    </TableCell>
                    <TableCell>
                      <PurchaseOrderStatusBadge status={purchaseOrder.status} />
                    </TableCell>
                    <TableCell>
                      <ActionButton
                        purchaseOrder={purchaseOrder}
                        balancingRecords={balancingRecords}
                        onEditPurchaseOrder={onEditPurchaseOrder}
                        onOpenProgress={onOpenProgress}
                        onOpenSupplierBalancing={onOpenSupplierBalancing}
                        onCancelPurchaseOrder={onCancelPurchaseOrder}
                        isCanceling={cancelingPurchaseOrderId === purchaseOrder.id}
                      />
                    </TableCell>
                  </tr>
                );
              })
            ) : (
              <TableEmptyOverlay.Row colSpan={9} />
            )}
          </tbody>
        </table>
      </div>
      {!purchaseOrders.length ? (
        <TableEmptyOverlay>
          <EmptyState />
        </TableEmptyOverlay>
      ) : null}

      <div className="grid gap-3 p-4 md:hidden">
        {purchaseOrders.length ? (
          purchaseOrders.map((purchaseOrder) => {
            const branchValue = formatBranchValue(purchaseOrder, warehouseLocationOptions);

            return (
              <article
                key={purchaseOrder.id}
                className={`rounded-[18px] border p-3 transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.11)] ${
                  isDeliveryToday(purchaseOrder.deliveryDate)
                    ? 'border-[#e9c06e] bg-[#fff6e2] hover:bg-[#ffefc8]'
                    : 'border-[var(--theme-border)] bg-[var(--theme-surface-muted)] hover:bg-[var(--theme-surface-soft)]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <strong className="block text-[1rem] text-[var(--theme-text)]">
                      {formatPurchaseOrderCode(purchaseOrder)}
                    </strong>
                    <span className="mt-1 block text-[0.92rem] text-[var(--theme-text-muted)]">
                      {formatSupplierDisplay(purchaseOrder.sacksMark, purchaseOrder.supplierName)}
                    </span>
                    <span className="mt-1 block text-[0.92rem] text-[var(--theme-text-muted)]">
                      P.O. Date: {formatDate(purchaseOrder.poDate)}
                    </span>
                  </div>

                  <ActionButton
                    purchaseOrder={purchaseOrder}
                    balancingRecords={balancingRecords}
                    onEditPurchaseOrder={onEditPurchaseOrder}
                    onOpenProgress={onOpenProgress}
                    onOpenSupplierBalancing={onOpenSupplierBalancing}
                    onCancelPurchaseOrder={onCancelPurchaseOrder}
                    isCanceling={cancelingPurchaseOrderId === purchaseOrder.id}
                  />
                </div>

                <div className="mt-3 grid gap-1.5 text-[0.88rem] text-[var(--theme-text-subtle)]">
                  <PurchaseOrderMeta
                    label="Price"
                    value={formatPurchaseOrderPrice(getDisplayPrice(purchaseOrder))}
                  />
                  <PurchaseOrderMeta
                    label="Quantity"
                    value={formatStockValue(getDisplayQuantity(purchaseOrder), purchaseOrder.stockUnit)}
                  />
                  <PurchaseOrderMeta
                    label="Branch"
                    value={
                      <span className="block max-w-[110px] truncate" title={branchValue}>
                        {branchValue}
                      </span>
                    }
                  />
                  <PurchaseOrderMeta
                    label="Delivery Date"
                    value={<DeliveryDateValue value={purchaseOrder.deliveryDate} />}
                  />
                  <PurchaseOrderMeta
                    label="Status"
                    value={<PurchaseOrderStatusBadge status={purchaseOrder.status} />}
                  />
                </div>
              </article>
            );
          })
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState />
          </div>
        )}
      </div>
    </div>
  );
}

function TableHeading({ children }) {
  return (
    <th className="whitespace-nowrap px-3 py-2.5 text-left text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[#222222] lg:px-4">
      {children}
    </td>
  );
}

function DeliveryDateSortIcon({ sort }) {
  if (sort === 'desc') {
    return <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />;
  }

  if (sort === 'asc') {
    return <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />;
  }

  return <ArrowUpDown className="h-3.5 w-3.5" aria-hidden="true" />;
}

function getDeliveryDateSortLabel(sort) {
  if (sort === 'desc') {
    return 'Delivery Date sorted newest to oldest. Click to sort oldest to newest.';
  }

  if (sort === 'asc') {
    return 'Delivery Date sorted oldest to newest. Click to return to normal sort.';
  }

  return 'Sort Delivery Date newest to oldest.';
}

function PurchaseOrderMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[#2a2a2a]">{value}</div>
    </div>
  );
}

function DeliveryDateValue({ value }) {
  const isToday = isDeliveryToday(value);

  return (
    <span className="inline-flex items-center gap-2">
      <span>{formatDate(value)}</span>
      {isToday ? (
        <span className="rounded-full border border-[#f0c46d] bg-[#fff0c7] px-1.5 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.06em] text-[#8a5a00]">
          Today
        </span>
      ) : null}
    </span>
  );
}

function ActionButton({
  purchaseOrder,
  balancingRecords = [],
  onEditPurchaseOrder,
  onOpenProgress,
  onOpenSupplierBalancing,
  onCancelPurchaseOrder,
  isCanceling,
}) {
  const purchaseOrderCode = formatPurchaseOrderCode(purchaseOrder);
  const isCancelled = purchaseOrder.status === 'Cancelled';
  const isCompleted = purchaseOrder.status === 'Completed';
  const canOpenSupplierBalancing =
    purchaseOrder.status === 'Completed' ||
    (purchaseOrder.status === 'Received' && Boolean(purchaseOrder.balancingReadyAt));
  const needsCompletedConfirmation = isPurchaseOrderReadyForCompletedConfirmation(
    purchaseOrder,
    balancingRecords
  );
  const canCancel = ['Initial', 'In Progress'].includes(purchaseOrder.status);
  const areRowActionsDisabled = isCancelled || isCanceling;
  const isReleaseTagged = Boolean(purchaseOrder.hasReleaseItems);
  const cancelTitle = isReleaseTagged
    ? `${purchaseOrderCode} is tagged for release/pre-release.`
    : `Cancel ${purchaseOrderCode}`;

  return (
    <div className="flex items-center gap-1.5">
      <ActionIconButton
        tone="success"
        onClick={() => onOpenProgress(purchaseOrder)}
        label={
          needsCompletedConfirmation
            ? `Confirm completed for ${purchaseOrderCode}`
            : `Update progress for ${purchaseOrderCode}`
        }
        title={
          needsCompletedConfirmation
            ? `Paid balancing is ready. Open ${purchaseOrderCode} to confirm completed.`
            : `Update progress for ${purchaseOrderCode}`
        }
        disabled={areRowActionsDisabled}
      >
        <span className="relative inline-flex">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          {needsCompletedConfirmation ? (
            <span className="absolute -right-1.5 -top-1.5 flex h-3 w-3" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#e03f3f] opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full border border-white bg-[#e03f3f]" />
            </span>
          ) : null}
        </span>
      </ActionIconButton>
      <ActionIconButton
        onClick={() => onEditPurchaseOrder(purchaseOrder)}
        label={`${isCompleted ? 'View' : 'Edit'} ${purchaseOrderCode}`}
        title={`${isCompleted ? 'View' : 'Edit'} ${purchaseOrderCode}`}
        disabled={areRowActionsDisabled}
      >
        {isCompleted ? (
          <Eye className="h-4 w-4" aria-hidden="true" />
        ) : (
          <PencilLine className="h-4 w-4" aria-hidden="true" />
        )}
      </ActionIconButton>
      {canOpenSupplierBalancing ? (
        <ActionIconButton
          tone="success"
          onClick={() => onOpenSupplierBalancing?.(purchaseOrder)}
          label={`Open Supplier Balancing for ${purchaseOrderCode}`}
          title={`Open Supplier Balancing for ${purchaseOrderCode}`}
          disabled={areRowActionsDisabled}
        >
          <HandCoins className="h-4 w-4" aria-hidden="true" />
        </ActionIconButton>
      ) : null}
      <ActionIconButton
        tone="danger"
        onClick={() => onCancelPurchaseOrder(purchaseOrder)}
        label={`Cancel ${purchaseOrderCode}`}
        title={cancelTitle}
        disabled={areRowActionsDisabled || !canCancel}
      >
        <XCircle className="h-4 w-4" aria-hidden="true" />
      </ActionIconButton>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <ClipboardList className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        No purchase orders found
      </strong>
    </div>
  );
}

function isPurchaseOrderReadyForCompletedConfirmation(purchaseOrder, balancingRecords = []) {
  if (
    purchaseOrder.status !== 'Received' ||
    !purchaseOrder.balancingReadyAt ||
    !purchaseOrder.id
  ) {
    return false;
  }

  const purchaseOrderId = String(purchaseOrder.id);

  return balancingRecords.some(
    (record) =>
      record.status === 'Paid' &&
      (record.purchaseOrderIds || []).some(
        (recordPurchaseOrderId) => String(recordPurchaseOrderId) === purchaseOrderId
      )
  );
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function isDeliveryToday(value) {
  if (!value) {
    return false;
  }

  return String(value).slice(0, 10) === getTodayInManila();
}

function getTodayInManila() {
  const dateParts = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Asia/Manila',
    year: 'numeric',
  }).formatToParts(new Date());
  const mappedParts = dateParts.reduce((parts, part) => {
    parts[part.type] = part.value;
    return parts;
  }, {});

  return `${mappedParts.year}-${mappedParts.month}-${mappedParts.day}`;
}

function formatPurchaseOrderCode(purchaseOrder) {
  return purchaseOrder.poCode || `PO-${String(purchaseOrder.id).padStart(4, '0')}`;
}

function formatStockValue(quantity, stockUnit) {
  const unitLabel = stockUnit === 'kg' ? 'kg' : 'sacks';
  return `${Number(quantity)} ${unitLabel}`;
}

function formatPurchaseOrderPrice(value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return '-';
  }

  return Number(value).toLocaleString('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getDisplayPrice(purchaseOrder) {
  return purchaseOrder.actualPrice ?? purchaseOrder.price;
}

function getDisplayQuantity(purchaseOrder) {
  const actualReceivedQuantity = getActualReceivedQuantityFromItems(purchaseOrder);

  if (
    ['Received', 'Completed'].includes(purchaseOrder.status) &&
    actualReceivedQuantity !== null
  ) {
    return actualReceivedQuantity;
  }

  return purchaseOrder.receivedQuantity ?? purchaseOrder.quantity;
}

function getActualReceivedQuantityFromItems(purchaseOrder) {
  const receivingItems = Array.isArray(purchaseOrder.receivingItems)
    ? purchaseOrder.receivingItems
    : [];

  if (!receivingItems.length) {
    return null;
  }

  const isSackPurchaseOrder = purchaseOrder.stockUnit !== 'kg';
  const receivedTotal = receivingItems.reduce((total, item) => {
    const isRejectItem = String(item.category || '').trim() === 'Reject';

    if (isSackPurchaseOrder && isRejectItem) {
      return total;
    }

    const quantity = Number(item.quantity);
    const quantityKg = Number(item.quantityKg);
    const itemQuantity =
      Number.isFinite(quantity) && quantity > 0
        ? quantity
        : Number.isFinite(quantityKg)
          ? quantityKg
          : 0;

    return total + itemQuantity;
  }, 0);

  return Math.round(receivedTotal * 100) / 100;
}

function formatSupplierDisplay(sacksMark, supplierName) {
  const normalizedSacksMark = String(sacksMark || '').trim();
  const normalizedSupplierName = String(supplierName || '').trim();

  if (normalizedSacksMark && normalizedSupplierName) {
    return `${normalizedSacksMark} - ${normalizedSupplierName}`;
  }

  return normalizedSacksMark || normalizedSupplierName || '-';
}

function formatBranchValue(purchaseOrder, warehouseLocationOptions = []) {
  const warehouseLocation = String(purchaseOrder?.warehouseLocation || '').trim();

  if (!warehouseLocation) {
    return '-';
  }

  const warehouseLocationOption = warehouseLocationOptions.find(
    (option) => String(option.value || '').trim() === warehouseLocation
  );
  const branchName = String(warehouseLocationOption?.branchName || '').trim();

  return branchName ? `${branchName} - ${warehouseLocation}` : warehouseLocation;
}

export default PurchaseOrderTable;
